import React from 'react';
import {
  Button,
  TextInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
  Tooltip,
} from '@c2fo/liquidity';
import type { DivisionWithPotentialBid } from '../utils/templateRenderer';

const LOAD_SUGGESTED_OFFERS_TOOLTIP =
  'This loads supplier specific recommendations from the data science team. Suggestions account for current market conditions and are updated regularly.';

interface OfferSimulatorControlsProps {
  globalPotentialBid: { maxBid: number | null; isDiscountBidding: boolean };
  onGlobalPotentialBidChange: (bid: { maxBid: number | null; isDiscountBidding: boolean }) => void;
  onSetAllPotentialBids: () => void;
  onGetOfferSuggestions: () => Promise<void>;
  simulateOffersLoading: boolean;
  offerSuggestionsLoading: boolean;
  divisionsWithBids: DivisionWithPotentialBid[];
  hasCheckableRows: boolean;
  showMissingOfferSuggestionsWarning: boolean;
}

export const OfferSimulatorControls: React.FC<OfferSimulatorControlsProps> = ({
  globalPotentialBid,
  onGlobalPotentialBidChange,
  onSetAllPotentialBids,
  onGetOfferSuggestions,
  simulateOffersLoading,
  offerSuggestionsLoading,
  divisionsWithBids,
  hasCheckableRows,
  showMissingOfferSuggestionsWarning,
}) => {
  return (
    <>
      <div className="flex gap-4 items-end mb-6">
        <div className="flex flex-col gap-1">
          <Label htmlFor="globalPotentialBid">Bid</Label>
          <TextInput
            id="globalPotentialBid"
            type="number"
            step="any"
            min="0"
            max="100"
            value={globalPotentialBid.maxBid ?? ''}
            onChange={(e) =>
              onGlobalPotentialBidChange({ ...globalPotentialBid, maxBid: parseFloat(e.target.value) || null })
            }
            className="w-24"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="globalPotentialBidType">Type</Label>
          <Select
            value={globalPotentialBid.isDiscountBidding ? 'true' : 'false'}
            onValueChange={(value) =>
              onGlobalPotentialBidChange({ ...globalPotentialBid, isDiscountBidding: value === 'true' })
            }
          >
            <SelectTrigger id="globalPotentialBidType" className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="false">APR</SelectItem>
              <SelectItem value="true">Discount</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="primary" onClick={onSetAllPotentialBids} disabled={globalPotentialBid.maxBid === null}>
          Apply to All Divisions
        </Button>
        <div className="ml-auto">
          <Tooltip.Provider delayDuration={300}>
            <Tooltip>
              <Tooltip.Trigger asChild>
                <span className="inline-block">
                  <Button
                    variant="primary"
                    onClick={onGetOfferSuggestions}
                    disabled={
                      simulateOffersLoading ||
                      offerSuggestionsLoading ||
                      divisionsWithBids.length === 0 ||
                      !hasCheckableRows
                    }
                    loading={offerSuggestionsLoading || simulateOffersLoading}
                  >
                    Load Suggested Offers
                  </Button>
                </span>
              </Tooltip.Trigger>
              <Tooltip.Content side="left" variant="dark" className="max-w-xs">
                {LOAD_SUGGESTED_OFFERS_TOOLTIP}
              </Tooltip.Content>
            </Tooltip>
          </Tooltip.Provider>
        </div>
      </div>

      {showMissingOfferSuggestionsWarning && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800">
          One or more offer suggestion could not be found. The potential bids highlighted in red will need to be updated
          manually.
        </div>
      )}
    </>
  );
};
